import { urlEscaped } from '@app/utils/urlEscaped'

import { createApiAction } from '@app/packages/api/utils'
import type { ApiAcademyCart, ApiAcademyCourse, ApiAcademyInvoice, ApiAcademyLesson, ApiAcademyLessonRecord } from '@app/packages/api/v2/types/entities'
import type { AcademyEventName, ApiDataResponse } from '@app/packages/api/v2/types/misc'
import type { AcademyCartPurchaseRequest, AcademyInvoiceCreateRequest } from '@app/packages/api/v2/types/params'
import { v2Path } from '@app/packages/api/v2/utils'

/** Every lesson read asks for the same relationships; the cards need all of them. */
const LESSON_INCLUDE = 'course,sitter,pictures,sitter.meta_tags,sitter.avatars'

export const postAcademyCarts = (request: AcademyCartPurchaseRequest) =>
  createApiAction('postAcademyCarts', dispatch =>
    dispatch<ApiDataResponse<ApiAcademyCart>>({ method: 'POST', endpoint: '/api/v2/academy/carts', body: JSON.stringify(request) })
  )

/** The sanofi event has its own path; every other event is a filter on the shared one. */
export const getAcademyLessons = (event?: 'sanofi' | AcademyEventName, course_id?: string) =>
  createApiAction('getAcademyLessons', dispatch => {
    const realEvent = event === 'academy' ? undefined : event

    return dispatch<ApiDataResponse<ApiAcademyLesson[]>>({
      method: 'GET',
      endpoint:
        realEvent === 'sanofi'
          ? v2Path('/api/v2/academy/event/sanofi/lessons', { include: LESSON_INCLUDE })
          : v2Path('/api/v2/academy/lessons', { event: realEvent, course_id, include: LESSON_INCLUDE }),
    })
  })

export const getAcademyLessonsById = (id: string, event?: 'sanofi') =>
  createApiAction('getAcademyLessonsById', dispatch =>
    dispatch<ApiDataResponse<ApiAcademyLesson>>({
      method: 'GET',
      endpoint: v2Path(event ? urlEscaped`/api/v2/academy/event/${event}/lessons/${id}` : urlEscaped`/api/v2/academy/lessons/${id}`, {
        include: LESSON_INCLUDE,
      }),
    })
  )

export const getAcademyCourses = (event?: AcademyEventName, max_age_days?: number) =>
  createApiAction('getAcademyCourses', dispatch =>
    dispatch<{ data: ApiAcademyCourse[] }>({
      method: 'GET',
      endpoint: v2Path('/api/v2/academy/courses', { event: event === 'academy' ? undefined : event, max_age_days }),
    })
  )

export const getAcademyCourseById = (course_id: string) =>
  createApiAction('getAcademyCourseById', dispatch =>
    dispatch<ApiDataResponse<ApiAcademyCourse>>({ method: 'GET', endpoint: urlEscaped`/api/v2/academy/courses/${course_id}` })
  )

export const postAcademyInvoices = (request: AcademyInvoiceCreateRequest) =>
  createApiAction('postAcademyInvoices', dispatch =>
    dispatch<{ data: ApiAcademyInvoice }>({ method: 'POST', endpoint: '/api/v2/academy/invoices', body: JSON.stringify(request) })
  )

export const postAcademyInvoicesByIdPayment = (invoiceId: string) =>
  createApiAction('postAcademyInvoicesByIdPayment', dispatch =>
    dispatch<unknown>({ method: 'POST', endpoint: urlEscaped`/api/v2/academy/invoices/${invoiceId}/payment` })
  )

export const getAcademyProfileLessonRecords = (query: {
  email_token?: string
  page: number
  per_page: number
  order_by: 'start_time'
  order_dir: 'asc' | 'desc'
  upcoming?: boolean
}) =>
  createApiAction('getAcademyProfileLessonRecords', dispatch =>
    dispatch<ApiDataResponse<ApiAcademyLessonRecord[]> & { meta: { total: number; total_pages: number; current_page: number } }>({
      method: 'GET',
      endpoint: v2Path('/api/v2/academy/profile/lesson_records', query),
    })
  )
